import * as React from 'react';

import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import useRouter from 'js/lib/useRouter';
import user from 'js/lib/user';

import { Loading } from '@coursera/coursera-ui';

import type { Stores } from 'bundles/help-redirects/appEnv';

import { performRedirect } from '../utils/redirection';
import { trackHelpRedirect } from '../utils/tracking';
import { getRedirectInfo } from '../utils/urlMapper';

// IMPORTANT: We need to retain this code indefinitely, since all help article hyperlinks should be using URLs that pass through this code.
// Please see this link for more details: https://go.dkandu.me/help-links-format

type Props = {
  requestCountryCode?: string | null;
  csrfToken?: string | null;
};

type RouterLocation = {
  hostname: string;
  pathname: string;
  search: string;
};

const getRequestUrl = (routerLocation: RouterLocation): string =>
  'https://' + routerLocation.hostname + routerLocation.pathname + routerLocation.search; // this would simply be window.location.href, but that's not available in SSR

// The function below is exported so we can write tests to check that it works in spite of URL encoding etc.
/**
 * When you link from one SF article to another, you don't want clicking that link to go through SSO redirects or it will be a very slow experience.  But you might still want to use these redirected links because they're more static.
 * For this use case, you can add ?skipsso=1 to the Zendesk-style permanent article URL (force_bypass_sso=1 was just too long for friendly URLs)
 */
export const getIsForceBypassSso = (requestUrl: string, sourceUrl: string): boolean => {
  return requestUrl.indexOf('skipsso=1') > 0 || sourceUrl.indexOf('skipsso=1') > 0;
};

const HelpRedirectsApp = (_props: Props) => {
  const router = useRouter();

  // Do NOT use window.location here as it is not compatible with SSR
  const sourceUrl = router.location.query?.sourceUrl || ''; // This URL param value indicates the place the user originally had in their address bar (i.e. the Zendesk article URL or article permalink that needs to be redirected)
  const requestUrl = getRequestUrl(router.location as unknown as RouterLocation); // hostname property exists on router.location but is missing from TS definition
  const isAuthenticated = user.isAuthenticatedUser();
  const forceBypassSso = getIsForceBypassSso(requestUrl, sourceUrl);

  // Do NOT move the following code into a useEffect!  Server-side (SSR) redirection is what we want, and useEffect hooks do not run at all in SSR.
  const redirectInfo = getRedirectInfo(sourceUrl, requestUrl, isAuthenticated, forceBypassSso);
  trackHelpRedirect(redirectInfo);
  performRedirect(redirectInfo.location, redirectInfo.statusCode);

  // performRedirect() will always perform a redirect
  // The following should not get displayed because by now the learner should have been redirected somewhere else by a server-side 301/302 response
  // In case client-side redirection is happening as a fallback (e.g. there was SSR failure) they will see this UI while the browser is executing location.replace()
  return <Loading />;
};

export default connectToStores<Props, {}, Stores>(['ApplicationStore'], ({ ApplicationStore }, props) => ({
  ...props,
  csrfToken: ApplicationStore.getState().csrfToken,
  requestCountryCode: ApplicationStore.getState().requestCountryCode,
}))(HelpRedirectsApp);
